/**
 * CSS for the self-contained garden metrics report. Categorical flower-instance colors and
 * chart chrome/ink values are the validated defaults from the workspace's dataviz skill
 * (`references/palette.md`) — both light and dark steps are the *selected* dark-mode set,
 * not an automatic filter, and are exposed as CSS custom properties so the client script can
 * read the currently-active values via `getComputedStyle` rather than duplicating the theme
 * decision in JS.
 */
export function reportStyles(): string {
  return `
:root {
  /* Chart/header chrome: muted moss-green surface + green-tinted chart gridlines/axis text.
     Scoped to the chart section and summary header only — see --surface-table for the table. */
  --surface-chart: #f6f8f3;
  --gridline-chart: #d7ddce;
  --text-secondary: #4d5749;

  /* Table section: distinct dark, muted umber/dirt surface (table + thead only). */
  --surface-table: #f5f0e7;
  /* Brown-tinted border for the table's own borders (scroll wrapper + header/row dividers) —
     distinct from --surface-table's fill so it reads as a border against that surface. */
  --border-table: #d9c9ae;

  /* Dedicated accent for interactive state (active toggle button, row highlight) — a muted,
     dark forest green. Deliberately separate from --series-1..8, which stay untouched. */
  --accent: #345940;

  --page-plane: #f9f9f7;
  --text-primary: #0b0b0b;
  --text-muted: #898781;
  --gridline: #e1e0d9;
  --baseline: #c3c2b7;
  --border-ring: rgba(11, 11, 11, 0.10);
  --status-critical: #d03b3b;

  --series-1: #2a78d6;
  --series-2: #1baf7a;
  --series-3: #eda100;
  --series-4: #008300;
  --series-5: #4a3aa7;
  --series-6: #e34948;
  --series-7: #e87ba4;
  --series-8: #eb6834;
}

@media (prefers-color-scheme: dark) {
  :root {
    --surface-chart: #141a13;
    --gridline-chart: #2a332a;
    --text-secondary: #b9c2b0;

    --surface-table: #1e1811;
    --border-table: #4a3c29;

    --accent: #4a7256;

    --page-plane: #0d0d0d;
    --text-primary: #ffffff;
    --text-muted: #898781;
    --gridline: #2c2c2a;
    --baseline: #383835;
    --border-ring: rgba(255, 255, 255, 0.10);
    --status-critical: #e66767;

    --series-1: #3987e5;
    --series-2: #199e70;
    --series-3: #c98500;
    --series-4: #008300;
    --series-5: #9085e9;
    --series-6: #e66767;
    --series-7: #d55181;
    --series-8: #d95926;
  }
}

* { box-sizing: border-box; }

html, body {
  margin: 0;
  height: 100%;
  overflow: hidden;
  background: var(--page-plane);
  color: var(--text-primary);
  font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
  font-size: 13px;
}

.viz-root {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.summary-strip {
  flex: 0 0 auto;
  display: flex;
  gap: 24px;
  align-items: baseline;
  padding: 10px 16px;
  background: var(--surface-chart);
  border-bottom: 1px solid var(--border-ring);
}

.summary-strip h1 {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  margin-right: auto;
  color: var(--text-primary);
}

.summary-item .label {
  color: var(--text-muted);
  margin-right: 6px;
}

.summary-item .value {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--text-primary);
}

.summary-item .value.has-errors {
  color: var(--status-critical);
}

.chart-section {
  flex: 0 0 380px;
  display: flex;
  flex-direction: column;
  background: var(--surface-chart);
  border-bottom: 1px solid var(--border-ring);
  padding: 8px 16px 12px;
  min-height: 0;
}

.chart-controls {
  flex: 0 0 auto;
  display: flex;
  gap: 16px;
  align-items: center;
  padding-bottom: 8px;
}

.toggle-group {
  display: inline-flex;
  width: fit-content;
  border: 1px solid var(--border-ring);
  border-radius: 6px;
  overflow: hidden;
}

.toggle-group button {
  appearance: none;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font: inherit;
  padding: 4px 10px;
  cursor: pointer;
}

.toggle-group button + button {
  border-left: 1px solid var(--border-ring);
}

.toggle-group button.active {
  background: var(--accent);
  color: #ffffff;
}

.chart-hint {
  color: var(--text-muted);
  font-size: 11px;
}

.chart-canvas-wrap {
  flex: 1;
  min-height: 0;
  position: relative;
}

.table-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 12px 16px 16px;
  background: var(--surface-table);
}

.table-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border: 1px solid var(--border-table);
  border-radius: 6px;
  background: var(--surface-table);
}

table {
  border-collapse: collapse;
  width: 100%;
  font-size: 12px;
}

thead th {
  position: sticky;
  top: 0;
  background: var(--surface-table);
  color: var(--text-muted);
  text-align: left;
  font-weight: 600;
  padding: 6px 8px;
  border-bottom: 1px solid var(--border-table);
  white-space: nowrap;
  z-index: 1;
}

thead th[data-sort-key] {
  cursor: pointer;
  user-select: none;
}

thead th[data-sort-key]:hover {
  color: var(--text-primary);
}

thead th[data-sort-key]::after {
  content: "⇅";
  margin-left: 4px;
  color: var(--text-muted);
  opacity: 0.4;
}

thead th[data-sort-key].sort-active::after {
  content: attr(data-sort-arrow);
  margin-left: 4px;
  color: var(--text-primary);
  opacity: 1;
}

tbody td {
  padding: 5px 8px;
  border-bottom: 1px solid var(--border-table);
  color: var(--text-primary);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

td.wrap {
  white-space: normal;
  font-variant-numeric: initial;
}

tbody tr.error-row td {
  color: var(--status-critical);
}

tbody tr.error-row {
  box-shadow: inset 2px 0 0 var(--status-critical);
}

/* Faint per-row tint matching that row's flower color (--row-color, set inline per <tr>), so
   rows belonging to the same flower are visually associable even without the legend. */
tbody tr[data-flower-name] {
  background: color-mix(in srgb, var(--row-color, transparent) 8%, transparent);
}

tbody tr.row-highlight {
  background: color-mix(in srgb, var(--row-color, var(--accent)) 32%, transparent);
}

.empty-state {
  padding: 24px;
  color: var(--text-muted);
}
`;
}
